import { Box, Heading } from '@radix-ui/themes';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { ActionSelect } from 'components/layout/action-select';
import { Details } from 'components/layout/details';
import { EB } from 'components/layout/error-boundary';
import { CustodianForm } from 'components/main/routes/custodians/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { BaseDoc } from 'lib/interfaces/database/_base';
import { CustodianWhole } from 'lib/interfaces/database/custodian';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useState } from 'react';
import useCustodiansStore from 'stores/custodians';

export const Custodians = () => {
  const { data, create, load, update } = useCustodiansStore();

  const { perms } = useContext(AuthContext);
  const { custodians: crud } = perms;

  useEffect(() => {
    if (crud?.read) {
      load();
    }
  }, [crud?.read, load]);

  const [dialog, setDialog] = useState<DialogState>();

  return (
    <EB>
      <Heading>Custodians</Heading>
      <CommonTable
        defaultSort={{
          iCol: 1,
          asc: false,
        }}
        toolbarActions={[
          {
            hidden: !crud?.create,
            label: 'Create',
            onClick: () =>
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              }),
          },
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: CustodianWhole) => (
              <ActionSelect
                options={[
                  {
                    hidden: !crud?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Update: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: CustodianWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: CustodianWhole, b: CustodianWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Name',
            rowFn: (item: CustodianWhole) => item.name,
            sortFn: (a: CustodianWhole, b: CustodianWhole) =>
              SortUtil.strings(a.name, b.name),
          },
          {
            header: 'Notes',
            rowFn: (item: CustodianWhole) => (
              <Box style={{ maxWidth: '300px' }}>
                {item.notes && <Details>{item.notes}</Details>}
              </Box>
            ),
          },
        ]}
        data={data}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<CustodianForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<CustodianForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => update((dialog.data as BaseDoc).id, item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}
    </EB>
  );
};
